import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Search, RotateCcw, Save } from "lucide-react";
import { api } from "../lib/api";
import { TYPE_META } from "../components/AgendaItem";
import TaskForm from "../components/TaskForm";

const TYPE_OPTS = [
  { v: "task", label: "Ciclo" },
  { v: "deadline", label: "Scadenza" },
  { v: "urgency", label: "Urgenza" },
  { v: "event", label: "Evento" },
  { v: "call", label: "Call" },
  { v: "meeting", label: "Meeting" },
  { v: "appointment", label: "Appuntamento" },
  { v: "payment_reminder", label: "Pagamento" },
  { v: "followup", label: "Follow-up" },
  { v: "habit", label: "Abitudine" },
  { v: "note", label: "Nota" },
];

const STATUS_OPTS = [
  { v: "open", label: "Da fare" },
  { v: "in_progress", label: "In corso" },
  { v: "waiting", label: "In attesa" },
  { v: "completed", label: "Completato" },
  { v: "cancelled", label: "Annullato" },
];

const PRIORITY_OPTS = [
  { v: "critical", label: "Critica" },
  { v: "high", label: "Alta" },
  { v: "medium", label: "Media" },
  { v: "low", label: "Bassa" },
];

const DATE_FIELDS = [
  { v: "due_date", label: "Scadenza (due_date)" },
  { v: "scheduled_date", label: "Programmata (scheduled_date)" },
  { v: "completed_at", label: "Completato (completed_at)" },
  { v: "created_at", label: "Creazione (created_at)" },
];

const GROUP_OPTS = [
  { v: "day", label: "Per giorno" },
  { v: "week", label: "Per settimana" },
  { v: "month", label: "Per mese" },
  { v: "type", label: "Per tipo" },
  { v: "area", label: "Per area" },
  { v: "priority", label: "Per priorità" },
  { v: "none", label: "Piatto" },
];

const SORT_OPTS = [
  { v: "date_desc", label: "Data (più recenti prima)" },
  { v: "date_asc", label: "Data (più vecchie prima)" },
  { v: "priority", label: "Priorità" },
  { v: "created_desc", label: "Creazione (recenti)" },
];

const SHORTCUTS = [
  { key: "today", label: "Oggi", days: 0 },
  { key: "yesterday", label: "Ieri", days: -1, single: true },
  { key: "7d", label: "Ultimi 7gg", days: 7 },
  { key: "30d", label: "Ultimi 30gg", days: 30 },
  { key: "cm", label: "Questo mese", scope: "cm" },
  { key: "lm", label: "Mese scorso", scope: "lm" },
  { key: "cy", label: "Quest'anno", scope: "cy" },
];

function isoDate(d) { return d.toISOString().slice(0, 10); }

function applyShortcut(sc) {
  const today = new Date();
  if (sc.scope === "cm") return { from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDate(today) };
  if (sc.scope === "lm") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: isoDate(start), to: isoDate(end) };
  }
  if (sc.scope === "cy") return { from: `${today.getFullYear()}-01-01`, to: isoDate(today) };
  if (sc.single) {
    const d = new Date(); d.setDate(today.getDate() - Math.abs(sc.days));
    return { from: isoDate(d), to: isoDate(d) };
  }
  const from = new Date(); from.setDate(today.getDate() - sc.days);
  return { from: isoDate(from), to: isoDate(today) };
}

function MultiCheckbox({ options, values, onChange, testidPrefix }) {
  const set = new Set(values);
  const toggle = (v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...next]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testidPrefix + "-wrap"}>
      {options.map((o) => {
        const active = set.has(o.v);
        return (
          <button key={o.v} type="button" onClick={() => toggle(o.v)}
                  className="text-[11px] rounded-full border px-2.5 py-1 transition"
                  style={{
                    background: active ? "var(--pxos-primary-light)" : "white",
                    borderColor: active ? "var(--pxos-accent)" : "var(--pxos-border)",
                    fontWeight: active ? 600 : 400,
                  }}
                  data-testid={`${testidPrefix}-${o.v}`}
                  aria-pressed={active}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const DEFAULTS = {
  types: [], statuses: [], priorities: [], area_ids: [],
  project_id: "", date_field: "due_date", date_from: "", date_to: "",
  q: "", has_reminder: "", is_recurring: "",
  include_completed: true, group_by: "day", sort: "date_desc",
};

export default function TasksSearch() {
  const [params, setParams] = useSearchParams();
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savedName, setSavedName] = useState("");
  const [showSave, setShowSave] = useState(false);

  const state = useMemo(() => {
    const s = { ...DEFAULTS };
    if (params.get("types"))       s.types = params.get("types").split(",").filter(Boolean);
    if (params.get("statuses"))    s.statuses = params.get("statuses").split(",").filter(Boolean);
    if (params.get("priorities")) s.priorities = params.get("priorities").split(",").filter(Boolean);
    if (params.get("area_ids"))    s.area_ids = params.get("area_ids").split(",").filter(Boolean);
    if (params.get("project_id"))  s.project_id = params.get("project_id");
    if (params.get("date_field"))  s.date_field = params.get("date_field");
    if (params.get("date_from"))   s.date_from = params.get("date_from");
    if (params.get("date_to"))     s.date_to = params.get("date_to");
    if (params.get("q"))           s.q = params.get("q");
    if (params.get("has_reminder"))s.has_reminder = params.get("has_reminder");
    if (params.get("is_recurring"))s.is_recurring = params.get("is_recurring");
    if (params.get("group_by"))    s.group_by = params.get("group_by");
    if (params.get("sort"))        s.sort = params.get("sort");
    return s;
  }, [params]);

  const setState = (patch) => {
    const next = { ...state, ...patch };
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (Array.isArray(v)) { if (v.length) p.set(k, v.join(",")); }
      else if (v !== "" && v != null && !(typeof v === "boolean" && v === DEFAULTS[k])) {
        p.set(k, String(v));
      }
    });
    setParams(p);
  };

  useEffect(() => {
    api.listAreas().then(setAreas).catch(() => {});
    api.listProjects().then(setProjects).catch(() => {});
  }, []);

  const runSearch = () => {
    setLoading(true);
    const p = {};
    if (state.types.length)      p.types = state.types.join(",");
    if (state.statuses.length)   p.statuses = state.statuses.join(",");
    if (state.priorities.length) p.priorities = state.priorities.join(",");
    if (state.area_ids.length)   p.area_ids = state.area_ids.join(",");
    if (state.project_id)        p.project_id = state.project_id;
    if (state.date_field)        p.date_field = state.date_field;
    if (state.date_from)         p.date_from = state.date_from;
    if (state.date_to)           p.date_to = state.date_to;
    if (state.q)                 p.q = state.q;
    if (state.has_reminder === "yes") p.has_reminder = true;
    else if (state.has_reminder === "no") p.has_reminder = false;
    if (state.is_recurring === "yes") p.is_recurring = true;
    else if (state.is_recurring === "no") p.is_recurring = false;
    p.include_completed = state.include_completed;
    p.group_by = state.group_by;
    p.sort = state.sort;

    api.taskSearchAdvanced(p)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore ricerca"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { runSearch(); /* eslint-disable-next-line */ }, [params]);

  const areaById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);
  const subAreaOpts = useMemo(() => {
    // dependent multi: show sub-areas of selected areas
    const list = [];
    for (const a of areas) {
      if (state.area_ids.includes(a.id)) {
        for (const s of (a.sub_areas || [])) list.push({ v: s.id, label: `${a.name} · ${s.name}` });
      }
    }
    return list;
  }, [areas, state.area_ids]);

  const reset = () => setParams(new URLSearchParams());

  const saveView = async () => {
    if (!savedName.trim()) { toast.error("Nome vista obbligatorio"); return; }
    try {
      await api.createSavedView({ name: savedName, scope: "tasks", filters: state });
      toast.success("Vista salvata");
      setSavedName(""); setShowSave(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  return (
    <div className="space-y-5" data-testid="tasks-search-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Ricerca</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold inline-flex items-center gap-2">
            <Search size={26}/> Ricerca avanzata
          </h1>
          <p className="mt-2 text-sm opacity-70">Filtra e raggruppa: "tutte le call del mese scorso", "meeting Prime Vision ultimi 30 gg", ...</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="tasks-search-reset" title="Cancella tutti i filtri applicati">
            <RotateCcw size={14}/> Reset
          </button>
          <button onClick={() => setShowSave((s) => !s)} className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1" data-testid="tasks-search-save-toggle" title="Salva questa ricerca per riusarla">
            <Save size={14}/> Salva vista
          </button>
        </div>
      </div>

      {showSave && (
        <div className="pxos-card p-3 flex items-center gap-2" data-testid="tasks-search-save-panel">
          <input className="pxos-input flex-1" placeholder="Nome vista (es. Call ultimo mese)" value={savedName} onChange={(e) => setSavedName(e.target.value)} data-testid="tasks-search-save-name"/>
          <button className="pxos-btn-primary text-sm" onClick={saveView} data-testid="tasks-search-save-confirm">Salva</button>
        </div>
      )}

      <div className="pxos-card p-4 space-y-4" data-testid="tasks-search-filters">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70 mb-1.5">Tipo</div>
              <MultiCheckbox options={TYPE_OPTS} values={state.types} onChange={(v) => setState({ types: v })} testidPrefix="ts-type"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70 mb-1.5">Stato</div>
              <MultiCheckbox options={STATUS_OPTS} values={state.statuses} onChange={(v) => setState({ statuses: v })} testidPrefix="ts-status"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70 mb-1.5">Priorità</div>
              <MultiCheckbox options={PRIORITY_OPTS} values={state.priorities} onChange={(v) => setState({ priorities: v })} testidPrefix="ts-priority"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70 mb-1.5">Area</div>
              <MultiCheckbox
                options={areas.map((a) => ({ v: a.id, label: a.name }))}
                values={state.area_ids}
                onChange={(v) => setState({ area_ids: v })}
                testidPrefix="ts-area"
              />
            </div>
            {!!subAreaOpts.length && (
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70 mb-1.5">Sotto-area</div>
                <MultiCheckbox options={subAreaOpts} values={state.sub_area_ids || []} onChange={(v) => setState({ sub_area_ids: v })} testidPrefix="ts-subarea"/>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <label className="block text-xs">
              <span className="opacity-70">Ricerca testo (titolo + descrizione, FTS italiana)</span>
              <input className="pxos-input mt-1" placeholder="es. bilancio, revisione, marco" value={state.q} onChange={(e) => setState({ q: e.target.value })} data-testid="ts-q"/>
            </label>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <label className="block">
                <span className="opacity-70">Filtra per</span>
                <select className="pxos-input mt-1" value={state.date_field} onChange={(e) => setState({ date_field: e.target.value })} data-testid="ts-date-field">
                  {DATE_FIELDS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="opacity-70">Da</span>
                <input type="date" className="pxos-input mt-1" value={state.date_from} onChange={(e) => setState({ date_from: e.target.value })} data-testid="ts-date-from"/>
              </label>
              <label className="block">
                <span className="opacity-70">A</span>
                <input type="date" className="pxos-input mt-1" value={state.date_to} onChange={(e) => setState({ date_to: e.target.value })} data-testid="ts-date-to"/>
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest opacity-60 pr-1">Shortcut</span>
              {SHORTCUTS.map((sc) => (
                <button key={sc.key} onClick={() => { const r = applyShortcut(sc); setState({ date_from: r.from, date_to: r.to }); }}
                        className="rounded-full border px-2.5 py-1 text-[11px] hover:bg-black/[0.03]"
                        title={`Filtra date: ${sc.label}`}
                        data-testid={`ts-shortcut-${sc.key}`}>{sc.label}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="block">
                <span className="opacity-70">Progetto</span>
                <select className="pxos-input mt-1" value={state.project_id} onChange={(e) => setState({ project_id: e.target.value })} data-testid="ts-project">
                  <option value="">Tutti</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="opacity-70">Con reminder</span>
                <select className="pxos-input mt-1" value={state.has_reminder} onChange={(e) => setState({ has_reminder: e.target.value })} data-testid="ts-has-reminder">
                  <option value="">Tutti</option>
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="block">
                <span className="opacity-70">Ricorrente</span>
                <select className="pxos-input mt-1" value={state.is_recurring} onChange={(e) => setState({ is_recurring: e.target.value })} data-testid="ts-recurring">
                  <option value="">Tutti</option>
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="block">
                <span className="opacity-70">Includi completate</span>
                <select className="pxos-input mt-1" value={state.include_completed ? "yes" : "no"} onChange={(e) => setState({ include_completed: e.target.value === "yes" })} data-testid="ts-include-completed">
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="block">
                <span className="opacity-70">Raggruppa</span>
                <select className="pxos-input mt-1" value={state.group_by} onChange={(e) => setState({ group_by: e.target.value })} data-testid="ts-group-by">
                  {GROUP_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="opacity-70">Ordina</span>
                <select className="pxos-input mt-1" value={state.sort} onChange={(e) => setState({ sort: e.target.value })} data-testid="ts-sort">
                  {SORT_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </label>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="pxos-card p-6 text-center opacity-60" data-testid="tasks-search-loading">Ricerca in corso…</div>}
      {!loading && data && (
        <>
          <div className="text-xs opacity-70" data-testid="tasks-search-summary">
            {data.count} risultat{data.count === 1 ? "o" : "i"} · raggruppamento: <b>{data.group_by}</b>
          </div>
          {data.group_by === "none" ? (
            <FlatList items={data.items} areaById={areaById} onEdit={setEditing}/>
          ) : (
            <div className="space-y-4">
              {(data.groups || []).map((g) => (
                <section key={g.key} className="pxos-card p-3" data-testid="tasks-search-group">
                  <h2 className="font-display text-base font-semibold px-2 py-1 flex items-center justify-between">
                    <span data-testid="tasks-search-group-label">{g.label}</span>
                    <span className="text-xs opacity-60" data-testid="tasks-search-group-count">{g.count} risultat{g.count === 1 ? "o" : "i"}</span>
                  </h2>
                  <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
                    {g.items.map((it) => <SearchRow key={it.id} task={it} area={areaById[it.area_id]} onEdit={setEditing}/>)}
                  </div>
                </section>
              ))}
              {(!data.groups || !data.groups.length) && (
                <div className="pxos-card p-10 text-center opacity-70" data-testid="tasks-search-empty">
                  Nessun risultato con questi filtri.
                </div>
              )}
            </div>
          )}
        </>
      )}

      <TaskForm open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); runSearch(); }}/>
    </div>
  );
}

function SearchRow({ task, area, onEdit }) {
  const meta = TYPE_META[task.type] || TYPE_META.task;
  const Icon = meta.Icon;
  return (
    <div className="flex items-center gap-3 py-2 px-2 hover:bg-black/[0.02] rounded-lg cursor-pointer" onClick={() => onEdit(task)} data-testid="tasks-search-row">
      <div className="w-1 h-8 rounded" style={{ background: `var(${meta.varName})` }} aria-hidden />
      <Icon size={14} className="opacity-70" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">
          <span style={{ opacity: (task.status === "completed" || task.status === "cancelled") ? 0.6 : 1 }}>{task.title}</span>
        </div>
        <div className="text-[11px] opacity-60 flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="pxos-badge" style={{ background: `color-mix(in srgb, var(${meta.varName}) 15%, #FFFFFF)`, borderColor: `color-mix(in srgb, var(${meta.varName}) 40%, transparent)` }}>{meta.label}</span>
          {area && (<span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: area.color || "#B8B8B8" }}/> {area.name}</span>)}
          {task.due_date && <span>· Scad. {new Date(task.due_date).toLocaleDateString("it-IT")}</span>}
          {task.completed_at && <span>· Compl. {new Date(task.completed_at).toLocaleDateString("it-IT")}</span>}
          <span>· {task.status}</span>
        </div>
      </div>
    </div>
  );
}

function FlatList({ items, areaById, onEdit }) {
  if (!items?.length) return <div className="pxos-card p-10 text-center opacity-70">Nessun risultato.</div>;
  return (
    <div className="pxos-card p-3">
      <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
        {items.map((it) => <SearchRow key={it.id} task={it} area={areaById[it.area_id]} onEdit={onEdit}/>)}
      </div>
    </div>
  );
}
